import { isoZ, z } from 'my-zod'
import {
  enumOptionsToObject,
  formatCurrency,
  getSuperProductLine,
  ProductLine,
  SuperProductLine,
  SuperProductLinesNames
} from '../../internal'
import { dateFns, Iso } from 'iso-fns2'
import { match } from 'ts-pattern'

const rideInsurancePlanTypeOptions = ['Standard', 'Premium'] as const
const rideInsurancePlanTypes = enumOptionsToObject(rideInsurancePlanTypeOptions)

const rideInsuranceProgramOptions = ['damageProtectionPlan', 'adventuresAssure'] as const
const rideInsurancePrograms = enumOptionsToObject(rideInsuranceProgramOptions)
const rideInsuranceProgramLabels = {
  adventuresAssure: 'AdventureAssure',
  damageProtectionPlan: 'Damage Protection Plan'
} satisfies { [key in RideInsuranceConfigSchemas.RideInsuranceProgram]: string }

const superProductLinePrices = z.object({
  SXS: z.number().nullable(),
  ACE: z.number().nullable(),
  ATV: z.number().nullable(),
  IND: z.number().nullable(),
  PON: z.number().nullable(),
  SLG: z.number().nullable(),
  SNO: z.number().nullable()
} satisfies { [key in SuperProductLine]: z.ZodType })

function getFallbackPricingForRideInsurance(insurance: z.infer<typeof superProductLinePrices>): {
  [key in SuperProductLine]: number
} {
  return {
    SXS: insurance.SXS ?? 15_00,
    ACE: insurance.ACE ?? 15_00,
    ATV: insurance.ATV ?? 15_00,
    IND: insurance.IND ?? 15_00,
    PON: insurance.PON ?? 15_00,
    SLG: insurance.SLG ?? 15_00,
    SNO: insurance.SNO ?? 15_00
  }
}

const rideInsuranceConfig = z.object({
  outfitterId: z.number(),
  versionId: z.number(),
  shortId: z.string(),
  config: z.discriminatedUnion('program', [
    superProductLinePrices.augment({
      program: z.literal(rideInsurancePrograms.damageProtectionPlan)
    }),
    superProductLinePrices.augment({
      program: z.literal(rideInsurancePrograms.adventuresAssure)
    })
  ]),
  taxPolicyIds: z.number().array(),
  createdDate: isoZ.instant()
})

const rideInsuranceConfigUpdate = rideInsuranceConfig.omit({ createdDate: true, shortId: true, versionId: true })

function getRideInsurancePrice(options: {
  rideInsurance: RideInsuranceConfigSchemas.rideInsuranceConfig['config']
  planType: RideInsuranceConfigSchemas.RideInsurancePlanType
  vehicles: { productLine: ProductLine }[]
  beginDate: Iso.Date
  endDate: Iso.Date
}): number {
  if (options.planType === 'Premium') {
    const days =
      dateFns
        .chain(options.beginDate)
        .until(options.endDate, { largestUnit: 'day', smallestUnit: 'day' })
        .getDays()
        .value() + 1
    const vehiclePrices = options.vehicles
      .map((v) => {
        if (!shouldSuperProductLineHaveRideInsurance(getSuperProductLine(v.productLine))) {
          throw new Error(`Product line ${v.productLine} should not have ride insurance`)
        } else {
          const price = getFallbackPricingForRideInsurance(options.rideInsurance)[getSuperProductLine(v.productLine)]
          return price
        }
      })
      .reduce((acc, cur) => acc + cur, 0)
    return options.rideInsurance.program === 'adventuresAssure' ? vehiclePrices * days : vehiclePrices
  } else {
    return 0
  }
}

function getSuperProductLineForVehicles(vehicles: { productLine: ProductLine | null }[]) {
  const relevant = vehicles.filter((v) => v.productLine)
  if (!relevant.length) {
    return null
  } else {
    const first = relevant.sort((a, b) => a.productLine!.localeCompare(b.productLine!))[0]
    const superProductLine = getSuperProductLine(first.productLine!)
    return superProductLine
  }
}

function getDeductibleAmountForRideInsurance({
  program,
  planType,
  vehicles
}: {
  program: RideInsuranceConfigSchemas.RideInsuranceProgram
  planType: RideInsuranceConfigSchemas.RideInsurancePlanType
  vehicles: { productLine: ProductLine | null }[]
}) {
  const superProductLine = getSuperProductLineForVehicles(vehicles)
  if (!superProductLine) {
    throw new Error('Cannot get ride insurance deductible for custom assets')
  }
  if (planType === 'Premium') {
    return match(superProductLine)
      .with('SXS', () => 1500_00)
      .with('ATV', () => 1500_00)
      .with('ACE', () => 1500_00)
      .with('SNO', () => 500_00)
      .with('SLG', () => 750_00)
      .with('PON', () => 750_00)
      .with('IND', () => 750_00)
      .exhaustive()
  } else if (planType === 'Standard') {
    return match(superProductLine)
      .with('SXS', () => 3000_00)
      .with('ATV', () => 3000_00)
      .with('ACE', () => 3000_00)
      .with('SNO', () => 1000_00)
      .with('SLG', () => 1500_00)
      .with('PON', () => 1500_00)
      .with('IND', () => 1500_00)
      .exhaustive()
  } else {
    throw new Error(`Unknown plan ${planType}`)
  }
}

function getDescriptionForRideInsurance({
  vehicles,
  deductible
}: {
  vehicles: { productLine: ProductLine | null }[]
  deductible: number
}) {
  const superProductLine = getSuperProductLineForVehicles(vehicles)
  if (!superProductLine) {
    throw new Error('Cannot get ride insurance description for custom assets')
  } else {
    return `The renter of this ${
      SuperProductLinesNames[superProductLine]
    } product is responsible for the first ${formatCurrency(
      deductible
    )} of any damages to the vehicle (including parts, labor, loss of use). All additional damage charges will be waived.`
  }
}

function shouldSuperProductLineHaveRideInsurance(superProductLine: SuperProductLine | 'Custom') {
  return superProductLine !== 'PON' && superProductLine !== 'Custom'
}

function shouldVehiclesHaveRideInsurance(vehicles: { productLine: ProductLine | null }[]) {
  const superProductLine = getSuperProductLineForVehicles(vehicles)
  return superProductLine ? shouldSuperProductLineHaveRideInsurance(superProductLine) : false
}

function getLabelForRideInsurance(rideInsurance: {
  planType: RideInsuranceConfigSchemas.RideInsurancePlanType
  program: RideInsuranceConfigSchemas.RideInsuranceProgram
}) {
  if (rideInsurance.program === 'adventuresAssure') {
    return rideInsurance.planType === 'Premium'
      ? 'AdventureAssure Upgraded Protection'
      : 'AdventureAssure Standard Protection'
  } else {
    return rideInsurance.planType === 'Premium' ? 'Premium Damage Protection Plan' : 'Standard Damage Protection Plan'
  }
}

export const RideInsuranceConfigSchemas = {
  rideInsuranceProgramOptions,
  rideInsurancePrograms,
  rideInsuranceProgramLabels,
  rideInsuranceConfig,
  rideInsuranceConfigUpdate,
  getFallbackPricingForRideInsurance,
  rideInsurancePlanTypes,
  rideInsurancePlanTypeOptions,
  getRideInsurancePrice,
  getDeductibleAmountForRideInsurance,
  getDescriptionForRideInsurance,
  shouldVehiclesHaveRideInsurance,
  getLabelForRideInsurance
}

export declare namespace RideInsuranceConfigSchemas {
  export type rideInsuranceConfig = z.infer<typeof rideInsuranceConfig>
  export type rideInsuranceConfigUpdate = z.infer<typeof rideInsuranceConfigUpdate>
  export type RideInsuranceProgram = (typeof rideInsuranceProgramOptions)[number]
  export type RideInsurancePlanType = (typeof rideInsurancePlanTypeOptions)[number]
}
